import React, { useCallback, useEffect, useMemo } from 'react';
import type { CollapseProps } from 'antd';
import { Collapse, Space } from 'antd';
import ImagesList from './ImagesList';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { VinInspectionItem } from '../inspectionTypes';
import type { InspectionAsset, InspectionAssetsResponse } from '../../../types/common/asset';
import {
  INSPECTION_BASE_VIEWS,
  isInspectionBaseView,
  isMobileInspectionTerminal,
  MOBILE_VIEW_NAME,
} from '../inspectionViewConstants';
import { useCollapseAllPanels } from '../hooks/useCollapseAllPanels';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';
import type { InspectionAssetTabKey } from '../inspectionTabs';

type AssetListItem = {
  id: string;
  src: string;
  title: string;
  raw: InspectionAsset;
};

/**
 * Backend (`playback/assets`) emits a full absolute URL, e.g.
 * `https://cdn.<domain>/raw/...` (wasabi-pipeline-backend-plan.md §6 defect 5).
 * Only prefix when the value is not already well-formed, so this stays safe
 * whether the API sends a scheme-less host+path or a full URL. `undefined`
 * (item.url is optional) falls through to an empty string.
 */
const toAbsoluteUrl = (url?: string): string => {
  if (!url) return '';
  return url.startsWith('https://') ? url : `https://${url}`;
};

interface InspectionAssetsPanelProps {
  onOpenModal?: (imageData?: InspectionMediaData, type?: InspectionMediaType) => void;
  assets: InspectionAssetsResponse | null;
  assetType: InspectionAssetTabKey;
  inspection: VinInspectionItem;
}

const InspectionAssetsPanel: React.FC<InspectionAssetsPanelProps> = ({ onOpenModal, assets, assetType, inspection }) => {
  const isMobileInspection = isMobileInspectionTerminal(inspection?.terminal);

  const allViewNames = useMemo(() => {
    if (isMobileInspection) return [MOBILE_VIEW_NAME];
    const extraViews: string[] = [];
    (assets?.items || []).forEach(item => {
      const view = typeof item.view === 'string' ? item.view : '';
      if (!view || view === 'Signature') return;
      if (!isInspectionBaseView(view) && !extraViews.includes(view)) {
        extraViews.push(view);
      }
    });
    return [...INSPECTION_BASE_VIEWS, ...extraViews];
  }, [isMobileInspection, assets?.items]);

  const groupedImages = useMemo(() => {
    const result: Record<string, AssetListItem[]> = allViewNames.reduce(
      (acc, view) => {
        acc[view] = [];
        return acc;
      },
      {} as Record<string, AssetListItem[]>
    );

    if (!assets?.items) return result;

    const typeFilter = assetType === 'defectFinder' ? 'video' : 'image';

    assets.items
      .filter(item => item.type === typeFilter)
      .forEach(item => {
        const imageData: AssetListItem = {
          id: `${item.view || MOBILE_VIEW_NAME}-${item.camera}`,
          src: toAbsoluteUrl(item.url),
          title: item.view || (isMobileInspection ? MOBILE_VIEW_NAME : 'N/A'),
          raw: item,
        };

        if (isMobileInspection && item.view !== 'Signature') {
          result[MOBILE_VIEW_NAME].push(imageData);
          return;
        }

        if (item.view && result[item.view]) {
          result[item.view].push(imageData);
        }
      });

    return result;
  }, [assets?.items, assetType, isMobileInspection, allViewNames]);

  const viewNames = useMemo(() => allViewNames.filter(view => groupedImages[view]?.length), [allViewNames, groupedImages]);

  const panelKeys = useMemo(() => viewNames.map((_, index) => String(index + 1)), [viewNames]);

  const { activeKeys, setActiveKeys, shouldShowCollapseAll, isPanelActive, togglePanel, toggleAll } = useCollapseAllPanels(
    panelKeys,
    isMobileInspection
  );

  useEffect(() => {
    setActiveKeys(viewNames.length ? ['1'] : []);
  }, [assetType, viewNames.length, setActiveKeys]);

  const renderExtraTabs = useCallback(
    (key: string, viewName: string) => (
      <Space size="middle" align="center">
        {viewName === viewNames[0] && (
          <span
            onClick={e => {
              e.stopPropagation();
              toggleAll();
            }}
            className="text-gray-600 cursor-pointer hover:text-gray-800 mr-2 text-sm font-medium">
            {shouldShowCollapseAll ? 'Collapse All' : 'Expand All'}
          </span>
        )}
        <span
          onClick={e => {
            e.stopPropagation();
            togglePanel(key);
          }}
          className="cursor-pointer text-gray-400 hover:text-gray-600">
          {isPanelActive(key) ? <UpOutlined /> : <DownOutlined />}
        </span>
      </Space>
    ),
    [isPanelActive, shouldShowCollapseAll, toggleAll, togglePanel, viewNames]
  );

  const viewItems: CollapseProps['items'] = useMemo(
    () =>
      viewNames.map((view, index) => ({
        key: String(index + 1),
        label: `${view} View`,
        children: (
          <ImagesList
            viewName={view}
            images={groupedImages[view]}
            onOpenModal={onOpenModal}
            assetType={assetType}
            isMobileInspection={isMobileInspection}
          />
        ),
        extra: renderExtraTabs(String(index + 1), view),
      })),
    [viewNames, groupedImages, onOpenModal, assetType, isMobileInspection, renderExtraTabs]
  );

  if (!viewItems?.length) {
    return <div className="text-center text-gray-400 py-10">No assets found for this inspection.</div>;
  }

  return (
    <Collapse
      items={viewItems}
      bordered={false}
      activeKey={activeKeys}
      onChange={keys => setActiveKeys(keys as string[])}
      className="bg-transparent"
      expandIcon={() => null}
    />
  );
};

export default InspectionAssetsPanel;
